import RolePermission from 'src/models/access-control/RolePermission'
import Circuit from 'src/models/circuit/Circuit'
import Model, { ID, MODEL_LEVEL } from 'src/models/model'
import User from 'src/models/user/User'

/**
 * These Role names are suggested for use in the application.
 * Names may be reserved for the following reasons:
 *  - The name is already used by another feature and it may cause confusion
 *  - The name is too generic and may be confused by common terminology, e.g. "User"
 *  - We want to be the only ones to create Roles with these names
 *  - We want to reserve the name for possible future use
 */
export const RESERVED_NAMES = {
  /** Circuits can have many AccountManagers */
  ACCOUNT_MANAGER: 'Account Manager',
  ADMIN: 'Admin',
  ANONYMOUS_USER: 'Anonymous User',
  EMPLOYEE: 'Employee',
  LOGGED_IN_USER: 'Logged In User',
  GUEST_USER: 'Guest User',
  SUPER_USER: 'Super User',
  USER: 'User'
}
RESERVED_NAMES.ALL = _.values(RESERVED_NAMES)

export const SUGGESTED_NAMES = {
  CINEMA_OPERATOR: 'Cinema Operator',
  CIRCUIT_ADMIN: 'Circuit Admin',
  SITE_ADMIN: 'Site Admin'
}
SUGGESTED_NAMES.ALL = _.values(SUGGESTED_NAMES)

export default class Role extends Model {
  static name = 'Role'

  static deletable = true

  static versioned = true

  static paginate = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Security]
  static manageAction = 'ManageCircuitRoles'

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true,
        required: true
      },

      name: {
        type: String,
        sortable: true,
        default: null
      },
      circuit: {
        type: Circuit,
        reference: true,
        output: true
      },
      rolePermissions: {
        type: [RolePermission],
        acceptsNestedAttributes: true,
        allowDestroy: true,
        reference: true
      },
      users: {
        type: [User],
        reference: true,
        encode (data) {
          data.userIds = _.map(this.users.data, 'id')
        },
        default: () => ([])
      },
      autoAssignAdmin: {
        type: Boolean,
        default: false
      },
      autoAssignEmployee: {
        type: Boolean,
        default: false
      }
    }
  }

  static isReservedRoleName (name) {
    const lowerCasedReservedNames = _.map(RESERVED_NAMES.ALL, _.toLower)
    return _.includes(lowerCasedReservedNames, _.toLower(name))
  }

  get userCount () {
    return this.users?.data?.length || 0
  }

  get permissionCount () {
    return this.rolePermissions?.data?.length || 0
  }

  get permissionActions () {
    return _.map(this.rolePermissions?.data, 'action') || []
  }

  togglePermission (permission) {
    if (_.find(this.rolePermissions.data, { action: permission.action })) {
      this.rolePermissions.data = _.filter(this.rolePermissions.data, ({ action }) => action !== permission.action)
    } else {
      this.rolePermissions.add(new RolePermission({ action: permission.action, permission }))
    }
  }
}
